import { useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';

import { fetchNewsDetail } from 'store/slices/pageSlice';
import { loadingStatus } from 'helpers/fetcher';
import Icon from 'components/Icon';

const PageNewsDetail = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { data, status } = useSelector(state => state.page.newsDetail);
	const { '*': news_id } = useParams();

	useEffect(() => {
		dispatch(fetchNewsDetail(news_id));
	}, [dispatch, news_id]);

	// Обработка HTML - проверяем на ошибки и фильтруем text_error
	const processedDescription = useMemo(() => {
		if (!data?.description) return '';
		// Удаляем text_error из описания
		let description = data.description;
		if (description.includes('text_error')) {
			description = description.replace(/text_error/gi, '').trim();
		}
		return description;
	}, [data?.description]);

	const hasError = useMemo(() => {
		if (!data?.description) return false;
		return data.description.includes('text_error');
	}, [data?.description]);

	// Форматирование даты
	const formatDate = dateStr => {
		if (!dateStr) return '';
		// Если дата в формате "23.10.2023", возвращаем как есть
		return dateStr;
	};

	// Форматирование даты публикации и обновления
	const getDateText = () => {
		if (!data?.posted) return '';
		const posted = formatDate(data.posted);
		const updated = data.updated ? formatDate(data.updated) : null;

		if (updated && updated !== posted) {
			return `${t('news.published')} ${posted} (${t('news.updated')} ${updated})`;
		}
		return `${t('news.published')} ${posted}`;
	};

	// Получаем количество просмотров
	const getViews = () => {
		if (!data?.viewed) return '0 просмотров';
		const views = data.viewed.replace(/[()]/g, '').trim();
		return views;
	};

	// Обработка ошибок загрузки изображений
	const contentRef = useRef(null);
	const errorHandlersRef = useRef([]);

	// Функция для группировки последовательных изображений в сетку
	const groupConsecutiveImages = container => {
		if (!container) return;

		// Получаем все прямые дочерние элементы
		const children = Array.from(container.children);
		let currentGroup = [];
		let groups = [];

		const isImageOnlyElement = el => {
			if (!el) return false;
			if (el.tagName === 'IMG') return true;
			// Проверяем содержит ли элемент только изображение(я)
			const imgs = el.querySelectorAll('img');
			if (imgs.length === 0) return false;
			// Получаем текст без учета вложенных элементов
			const clone = el.cloneNode(true);
			clone.querySelectorAll('img').forEach(img => img.remove());
			const textOnly = clone.textContent.replace(/\s+/g, '').trim();
			return textOnly.length === 0;
		};

		children.forEach(child => {
			if (isImageOnlyElement(child)) {
				currentGroup.push(child);
			} else {
				if (currentGroup.length >= 2) {
					groups.push([...currentGroup]);
				}
				currentGroup = [];
			}
		});

		// Не забываем последнюю группу
		if (currentGroup.length >= 2) {
			groups.push([...currentGroup]);
		}

		// Оборачиваем группы в контейнеры
		groups.forEach(group => {
			const wrapper = document.createElement('div');
			wrapper.className = 'images-grid';

			const firstElement = group[0];
			firstElement.parentNode.insertBefore(wrapper, firstElement);

			group.forEach(el => {
				wrapper.appendChild(el);
			});
		});
	};

	useEffect(() => {
		if (
			status === loadingStatus.SUCCEEDED &&
			data?.description &&
			contentRef.current
		) {
			// Группируем последовательные изображения
			groupConsecutiveImages(contentRef.current);

			const images = contentRef.current.querySelectorAll('img');
			errorHandlersRef.current = [];

			images.forEach(img => {
				const handleError = e => {
					const target = e?.target || img;
					target.style.backgroundColor = '#d9d9d9';
					target.style.minHeight = '200px';
					target.style.display = 'block';
					target.style.borderRadius = '20px';
					target.alt = t('common.imageNotLoaded');
					// Устанавливаем прозрачный пиксель вместо битого изображения
					target.onerror = null; // Предотвращаем бесконечный цикл
					target.src =
						"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'%3E%3C/svg%3E";
				};

				errorHandlersRef.current.push({ img, handleError });

				// Если изображение уже загружено с ошибкой
				if (img.complete && img.naturalHeight === 0) {
					handleError({ target: img });
				} else {
					img.addEventListener('error', handleError);
				}
			});

			// Очистка обработчиков при размонтировании
			return () => {
				errorHandlersRef.current.forEach(({ img, handleError }) => {
					img.removeEventListener('error', handleError);
				});
				errorHandlersRef.current = [];
			};
		}
	}, [status, data?.description]);

	return (
		<>
			<div className='region news-detail-section'>
				<div className='news-detail-wrapper'>
					{/* Breadcrumb */}
					<div className='contact-breadcrumb' style={{ marginTop: '64px' }}>
						<a
							href='/'
							onClick={e => {
								e.preventDefault();
								navigate('/');
							}}
							className='breadcrumb-link'
						>
							{t('common.home')}
						</a>
						<span className='breadcrumb-separator'></span>
						<a
							href='/news'
							onClick={e => {
								e.preventDefault();
								navigate('/news');
							}}
							className='breadcrumb-link'
						>
							{t('news.breadcrumb')}
						</a>
						<span className='breadcrumb-separator breadcrumb-separator-active'></span>
						<span className='breadcrumb-current'>
							{data?.title || data?.heading_title || t('news.articleTitle')}
						</span>
					</div>

					{/* Date and Views */}
					{status === loadingStatus.SUCCEEDED && data ? (
						<>
							<div
								className='news-detail-meta'
								style={{ marginBottom: '24px' }}
							>
								<h1 className='news-detail-date'>{getDateText()}</h1>
								<div className='news-detail-views'>
									<Icon
										name="icon-eye"
										className='news-detail-views-icon'
										ariaLabel={t('news.views')}
										ariaHidden={false}
									/>
									<span className='news-detail-views-text'>{getViews()}</span>
								</div>
							</div>

							{/* Title */}
							<h2
								className='news-detail-title'
								style={{ marginBottom: '24px' }}
							>
								{data.title || data.heading_title || t('news.articleTitleUpper')}
							</h2>

							{/* Content - изображения на всю ширину, текст в контейнере 860px */}
							{processedDescription && !hasError && (
								<div
									ref={contentRef}
									className='news-detail-content'
									dangerouslySetInnerHTML={{ __html: processedDescription }}
								/>
							)}

							{/* Если есть ошибка, показываем сообщение */}
							{hasError && (
							<div className='news-detail-error'>
								<p>
									{t('common.errorLoadArticle')}
								</p>
							</div>
							)}
						</>
					) : status === loadingStatus.LOADING ? (
						<>
							<Skeleton
								active
								paragraph={{ rows: 2 }}
								style={{ marginBottom: '24px' }}
							/>
							<Skeleton active paragraph={{ rows: 8 }} />
						</>
					) : (
						<div>{t('common.articleNotFound')}</div>
					)}
				</div>
			</div>

			{/* Quality Section */}
			{status === loadingStatus.SUCCEEDED && data && (
				<div
					className='region news-quality-section'
					style={{ marginBottom: '60px' }}
				>
				<h2 className='quality-title'>{t('quality.title')}</h2>
				<div className='quality-cards'>
					<div className='quality-card quality-card-gray'>
						<img
							src={`${process.env.PUBLIC_URL || ''}/images/icon-halal.webp`}
							alt=''
							className='quality-card-icon'
						/>
						<h4 className='quality-card-text'>
							{t('quality.noPork')}
						</h4>
					</div>
					<div className='quality-card quality-card-gray'>
						<img
							src={`${process.env.PUBLIC_URL || ''}/images/icon-eggs.webp`}
							alt=''
							className='quality-card-icon'
						/>
						<h4 className='quality-card-text'>{t('quality.naturalEggs')}</h4>
					</div>
					<div className='quality-card quality-card-gray'>
						<img
							src={`${process.env.PUBLIC_URL || ''}/images/icon-cow.webp`}
							alt=''
							className='quality-card-icon'
						/>
						<h4 className='quality-card-text'>{t('quality.ownSlaughter')}</h4>
					</div>
					<div className='quality-card quality-card-gray'>
						<img
							src={`${process.env.PUBLIC_URL || ''}/images/icon-nature.webp`}
							alt=''
							className='quality-card-icon'
						/>
						<h4 className='quality-card-text'>
							{t('quality.noPreservatives')}
						</h4>
					</div>
					<div
						className='quality-card quality-card-blue'
						style={{
							backgroundImage: `url(${
								process.env.PUBLIC_URL || ''
							}/images/bg-quality-card.webp)`,
							backgroundSize: 'cover',
							backgroundPosition: 'center',
							backgroundRepeat: 'no-repeat',
						}}
					>
						<Icon
							name="icon-dna"
							className='quality-card-icon'
						/>
						<h4 className='quality-card-text quality-card-text-white'>
							{t('quality.noGMO')}
						</h4>
					</div>
				</div>
				</div>
			)}
		</>
	);
};

export default PageNewsDetail;
